// src/seo/siteMeta.js
// Centralne dane SEO serwisu. Jedno miejsce prawdy dla tytułów, opisów,
// canonicali i danych strukturalnych (JSON-LD).
//
// Dodajesz nową trasę? Trzy miejsca: ROUTE_META tutaj, lista w
// scripts/seo-routes.mjs (prerender + sitemapa) oraz <Route> w src/App.js.

import {
  LICENCJE,
  LICENCJA_PO_SLUGU,
  PRZEDMIOTY,
  PROG_PROCENT,
  pytanWEgzaminie,
  przedmiotyLicencji,
} from '../data/licencje';

export const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://pilotuje.pl').replace(/\/$/, '');
export const SITE_NAME = 'Pilotuje.pl';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-pilotuje.png`;
export const CONTACT_EMAIL = 'kontakt@pilotuje.pl';

export const absoluteUrl = (path = '/') => {
  const clean = path.startsWith('/') ? path : `/${path}`;
  if (clean === '/') return `${SITE_URL}/`;
  return `${SITE_URL}${clean.replace(/\/$/, '')}`;
};

/* ------------------------------------------------------------------ */
/* Plany - używane też do danych strukturalnych Product/Offer          */
/* ------------------------------------------------------------------ */
export const getPlans = () =>
  [1, 2, 3]
    .map(i => ({
      id: i,
      days: parseInt(process.env[`REACT_APP_PLAN${i}_DAYS`], 10) || 0,
      price: parseFloat(process.env[`REACT_APP_PLAN${i}_PRICE`]) || 0,
      // cudzysłowy z .env obcinamy, a długie myślniki normalizujemy do dywizu
      // (obowiązuje w treściach serwisu, a wartości env łatwo o tym zapomnieć)
      desc: (process.env[`REACT_APP_PLAN${i}_DESC`] || '')
        .replace(/^['"“”]+|['"“”]+$/g, '')
        .replace(/[–—]/g, '-'),
    }))
    .filter(p => p.days > 0 && p.price > 0);

/* Plan oznaczany plakietką "Najczęściej wybierany". Cennik wyróżniał ostatni
   pakiet, a strona główna środkowy - dwie strony wskazywały różne plany jako
   najpopularniejszy. Jedno miejsce prawdy: pakiet 14-dniowy, a gdyby zniknął
   z konfiguracji, środkowy z dostępnych. */
export const FEATURED_PLAN_DAYS = 14;

export const getFeaturedPlanId = () => {
  const plans = getPlans();
  if (!plans.length) return null;
  const wybrany =
    plans.find(p => p.days === FEATURED_PLAN_DAYS) ||
    plans[Math.floor((plans.length - 1) / 2)];
  return wybrany.id;
};

export const getLowestPrice = () => {
  const prices = getPlans().map(p => p.price);
  return prices.length ? Math.min(...prices) : 49;
};

/* ------------------------------------------------------------------ */
/* Dane strukturalne                                                   */
/* ------------------------------------------------------------------ */
export const organizationJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: SITE_NAME,
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_URL}/pilotuje_logo.png`,
  },
  email: CONTACT_EMAIL,
  areaServed: 'PL',
  description:
    'Serwis z testami online przygotowującymi do egzaminów teoretycznych dla pilotów: licencji PPL(A) i LAPL(A), świadectwa kwalifikacji pilota samolotu ultralekkiego oraz uprawnień operatora drona A1/A3 i A2.',
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: CONTACT_EMAIL,
      availableLanguage: ['pl'],
    },
  ],
});

export const webSiteJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: SITE_URL,
  name: SITE_NAME,
  inLanguage: 'pl-PL',
  publisher: { '@id': `${SITE_URL}/#organization` },
});

export const faqJsonLd = items => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer },
  })),
});

/**
 * Dane strukturalne pojedynczego pytania egzaminacyjnego.
 *
 * Wcześniej strony /pytania/<slug> używały FAQPage z jednym elementem. Formalnie
 * poprawne, ale od sierpnia 2023 Google pokazuje wyniki rozszerzone FAQ wyłącznie
 * witrynom rządowym i medycznym - dla nas ten znacznik nie robił nic.
 *
 * Quiz z hasPart typu Question to schemat stworzony dokładnie pod tę treść:
 * suggestedAnswer dla dystraktorów, acceptedAnswer dla poprawnej, about dla
 * przedmiotu. Google obsługuje go w wynikach "zadania do przećwiczenia", na które
 * konkurencja w tej niszy nie patrzy.
 */
export const quizJsonLd = ({ slug, question, warianty, poprawna, wyjasnienie, przedmiot, licencje = [] }) => ({
  '@context': 'https://schema.org',
  '@type': 'Quiz',
  '@id': `${absoluteUrl(`/pytania/${slug}`)}#quiz`,
  url: absoluteUrl(`/pytania/${slug}`),
  name: question,
  inLanguage: 'pl-PL',
  educationalLevel: 'Egzamin teoretyczny ULC',
  ...(przedmiot ? { about: { '@type': 'Thing', name: przedmiot } } : {}),
  ...(licencje.length
    ? { educationalAlignment: licencje.map(nazwa => ({
        '@type': 'AlignmentObject',
        alignmentType: 'educationalSubject',
        targetName: nazwa,
      })) }
    : {}),
  hasPart: {
    '@type': 'Question',
    eduQuestionType: 'Multiple choice',
    name: question,
    text: question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: poprawna,
      ...(wyjasnienie ? { comment: { '@type': 'Comment', text: wyjasnienie } } : {}),
    },
    suggestedAnswer: warianty
      .filter(w => w !== poprawna)
      .map(w => ({ '@type': 'Answer', text: w })),
  },
  publisher: { '@id': `${SITE_URL}/#organization` },
});

export const breadcrumbJsonLd = crumbs => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: crumbs.map((c, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: c.name,
    item: absoluteUrl(c.path),
  })),
});

export const courseJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Course',
  name: 'Kurs online: testy do egzaminów teoretycznych dla pilotów',
  description:
    'Kurs w formie testów online przygotowujący do egzaminów teoretycznych na licencję PPL(A) i LAPL(A), świadectwo kwalifikacji pilota samolotu ultralekkiego oraz do egzaminów operatora drona A1/A3 i A2.',
  inLanguage: 'pl',
  url: absoluteUrl('/cennik'),
  provider: { '@type': 'Organization', name: SITE_NAME, sameAs: SITE_URL },
  offers: getPlans().map(p => ({
    '@type': 'Offer',
    category: 'Paid',
    price: p.price.toFixed(2),
    priceCurrency: 'PLN',
    availability: 'https://schema.org/InStock',
    url: absoluteUrl('/cennik'),
  })),
  hasCourseInstance: getPlans().map(p => ({
    '@type': 'CourseInstance',
    courseMode: 'online',
    courseWorkload: `P${p.days}D`,
    name: `Dostęp na ${p.days} ${p.days === 1 ? 'dzień' : 'dni'}`,
  })),
});

export const productJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'Dostęp do testów dla pilotów - Pilotuje.pl',
  description:
    'Dostęp online do bazy pytań egzaminacyjnych, trybu pojedynku i nauki po przedmiotach dla licencji PPL(A), LAPL(A), świadectwa kwalifikacji UACP oraz uprawnień operatora drona.',
  brand: { '@type': 'Brand', name: SITE_NAME },
  url: absoluteUrl('/cennik'),
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'PLN',
    lowPrice: getLowestPrice().toFixed(2),
    highPrice: Math.max(...getPlans().map(p => p.price), getLowestPrice()).toFixed(2),
    offerCount: getPlans().length || 3,
    availability: 'https://schema.org/InStock',
    url: absoluteUrl('/cennik'),
  },
});

/* ------------------------------------------------------------------ */
/* FAQ strony głównej - jedno źródło dla treści i dla JSON-LD          */
/* ------------------------------------------------------------------ */
export const HOME_FAQ = [
  {
    question: 'Jak wygląda egzamin teoretyczny na licencję PPL(A)?',
    answer:
      `Egzamin teoretyczny PPL(A) to dziewięć osobnych testów jednokrotnego wyboru, zdawanych przed Lotniczą Komisją Egzaminacyjną ULC: prawo lotnicze, ogólna wiedza o statku powietrznym, osiągi i planowanie lotu, człowiek - możliwości i ograniczenia, meteorologia, nawigacja, procedury operacyjne, zasady lotu oraz łączność. Każdy przedmiot zalicza się osobno przy ${PROG_PROCENT} procentach poprawnych odpowiedzi.`,
  },
  {
    question: 'Ile mam podejść do egzaminu i ile czasu na zaliczenie wszystkich przedmiotów?',
    answer:
      'Do jednego przedmiotu można podejść maksymalnie cztery razy, a wszystkie przedmioty trzeba zaliczyć w nie więcej niż sześciu sesjach egzaminacyjnych i w ciągu 18 miesięcy. Komplet zdanej teorii jest ważny 24 miesiące - w tym czasie trzeba przystąpić do egzaminu praktycznego.',
  },
  {
    question: 'Czym różni się LAPL(A) od PPL(A)?',
    answer:
      'Zakres przedmiotów teoretycznych jest ten sam, więc uczysz się z tej samej bazy pytań. Różnice dotyczą uprawnień: LAPL(A) obejmuje samoloty o masie startowej do 2000 kg i maksymalnie trzech pasażerów, jest ważna wyłącznie w państwach EASA i wymaga krótszego szkolenia praktycznego oraz orzeczenia lekarskiego LAPL zamiast klasy 2.',
  },
  {
    question: 'Czy przygotowujecie też do egzaminu na drona?',
    answer:
      'Tak. Mamy osobne bazy pytań do egzaminu online A1/A3, czyli 40 pytań w 30 minut z progiem 75 procent, oraz do dodatkowego egzaminu A2. Uwaga na zmianę stanu prawnego: krajowe scenariusze NSTS przestały obowiązywać 31 grudnia 2025 roku i w kategorii szczególnej stosuje się teraz europejskie scenariusze STS-01 i STS-02.',
  },
  {
    question: 'Skąd pochodzą pytania w bazie?',
    answer:
      'Pytania są autorskie. Napisaliśmy je pod program egzaminów teoretycznych określony przepisami Part-FCL, SERA oraz rozporządzeniami o bezzałogowych statkach powietrznych. Nie kopiujemy Krajowego Banku Pytań ULC, bo nie jest on udostępniany publicznie. Przy każdej odpowiedzi znajdziesz wyjaśnienie i wskazanie przepisu, z którego wynika.',
  },
  {
    question: 'Czy mogę sprawdzić testy przed zakupem?',
    answer:
      `Tak. Wersja demo pozwala rozegrać pojedynek na przykładowych pytaniach bez zakładania konta i bez płatności. Dostęp do pełnej bazy zaczyna się od ${getLowestPrice()} zł, płatność jest jednorazowa i nie odnawia się automatycznie.`,
  },
];

/* ------------------------------------------------------------------ */
/* Strony filarowe licencji                                            */
/* ------------------------------------------------------------------ */
/* Kolejność = kolejność w nawigacji. Używane do meta, linkowania
   wewnętrznego, stopki i generowania sitemapy. */
export const LICENCJA_PAGES = LICENCJE.map(l => ({
  ...l,
  naukaPath: `/nauka/${l.slug}`,
  title:
    l.rodzina === 'bsp'
      ? `Egzamin na drona ${l.krotko} 2026 - testy i pytania online`
      : `Testy na ${l.nazwa} 2026 - pytania egzaminacyjne ULC online`,
  description:
    l.rodzina === 'bsp'
      ? `Pytania do egzaminu operatora drona ${l.krotko}: ${l.arkusz.pytan} pytań, ${l.arkusz.minut} minut, próg ${PROG_PROCENT} procent. Wyjaśnienie przy każdej odpowiedzi, demo bez rejestracji.`
      : `Pytania egzaminacyjne na ${l.nazwa} z wyjaśnieniem odpowiedzi i podstawą prawną. Nauka po przedmiotach, tryb pojedynku i demo bez rejestracji.`,
}));

/* ------------------------------------------------------------------ */
/* Meta dla poszczególnych tras                                        */
/* ------------------------------------------------------------------ */
const lowest = getLowestPrice();

const metaLicencji = {};
LICENCJA_PAGES.forEach(l => {
  metaLicencji[l.path] = { title: l.title, description: l.description };
  metaLicencji[l.naukaPath] = {
    title: `Nauka do ${l.nazwa} - pytania według przedmiotów`,
    description: `Tryb nauki do ${l.nazwa}: pytania egzaminacyjne podzielone na ${przedmiotyLicencji(l).length} przedmiotów, z wyjaśnieniem odpowiedzi i podstawą prawną przy każdym pytaniu. W egzaminie zdajesz łącznie ${pytanWEgzaminie(l)} pytań.`,
  };
});

export const ROUTE_META = {
  '/': {
    title: 'Testy dla pilotów 2026 - PPL(A), ultralekkie i drony',
    description: `Ucz się na pytaniach do egzaminów teoretycznych ULC: PPL(A), LAPL(A), świadectwo kwalifikacji na samoloty ultralekkie oraz uprawnienia operatora drona A1/A3 i A2. Wyjaśnienie przy każdej odpowiedzi, dostęp od ${lowest} zł, demo bez rejestracji.`,
  },
  '/intro': {
    title: 'Test dla pilotów online - zmierz się z egzaminatorem',
    description:
      'Wybierz przeciwnika i rozegraj pojedynek na pytaniach egzaminacyjnych. Poprawna odpowiedź odbiera przeciwnikowi punkty życia, błędna kosztuje energię. Wersja demo działa bez rejestracji.',
  },
  '/egzamin-teoretyczny-ulc': {
    title: 'Egzamin teoretyczny w ULC - przebieg, terminy i zasady zaliczania',
    description:
      'Jak wygląda egzamin teoretyczny przed Lotniczą Komisją Egzaminacyjną: liczba pytań i czas dla każdego przedmiotu, próg 75 procent, limit czterech podejść i sześciu sesji oraz ważność zdanej teorii przez 24 miesiące.',
  },
  '/cennik': {
    title: `Cennik testów dla pilotów - dostęp już od ${lowest} zł`,
    description: `Wybierz pakiet dostępu do bazy pytań egzaminacyjnych dla pilotów i operatorów dronów. Ceny od ${lowest} zł, płatność jednorazowa, bez subskrypcji. Dostęp od razu po opłaceniu.`,
  },
  '/o-nas': {
    title: 'O nas - kto prowadzi Pilotuje.pl',
    description:
      'Kto tworzy Pilotuje.pl, jak powstaje nasza baza pytań egzaminacyjnych dla pilotów i jak aktualizujemy materiały przy zmianach przepisów lotniczych.',
  },
  '/kontakt': {
    title: 'Kontakt - Pilotuje.pl, testy dla pilotów',
    description:
      'Masz pytanie o dostęp do testów, płatność albo o sam egzamin teoretyczny w ULC? Napisz do nas - odpowiadamy zwykle w ciągu jednego dnia roboczego.',
  },
  '/zakup': {
    title: 'Zamówienie dostępu do testów dla pilotów',
    description: 'Dokończ zamówienie dostępu do bazy pytań egzaminacyjnych.',
    robots: 'noindex, follow',
  },
  '/podziekowanie': {
    title: 'Dziękujemy za zakup - Pilotuje.pl',
    description: 'Potwierdzenie zakupu dostępu do testów dla pilotów.',
    robots: 'noindex, nofollow',
  },
  '/login': {
    title: 'Logowanie - Pilotuje.pl',
    description: 'Zaloguj się, aby korzystać z wykupionego dostępu do testów.',
    robots: 'noindex, nofollow',
  },
  '/ranking': {
    title: 'Ranking - Pilotuje.pl',
    description: 'Wyniki graczy rozwiązujących testy egzaminacyjne dla pilotów.',
    robots: 'noindex, follow',
  },
  '/regulamin': {
    title: 'Regulamin serwisu Pilotuje.pl',
    description: 'Regulamin świadczenia usług drogą elektroniczną w serwisie Pilotuje.pl.',
    robots: 'noindex, follow',
  },
  '/polityka-prywatnosci': {
    title: 'Polityka prywatności - Pilotuje.pl',
    description: 'Zasady przetwarzania danych osobowych w serwisie Pilotuje.pl.',
    robots: 'noindex, follow',
  },
  ...metaLicencji,
};

const metaPrzedmiotu = (licencjaSlug, przedmiotSlug) => {
  const licencja = LICENCJA_PO_SLUGU[licencjaSlug];
  const przedmiot = PRZEDMIOTY.find(p => p.slug === przedmiotSlug);
  if (!licencja || !przedmiot) return null;
  return {
    title: `${przedmiot.nazwa} - pytania na ${licencja.nazwa}`,
    description: `${przedmiot.opis} Pytania egzaminacyjne z wyjaśnieniem odpowiedzi i podstawą prawną, w zakresie wymaganym na ${licencja.nazwa}.`,
  };
};

export const getRouteMeta = (rawPath = '/') => {
  const path = rawPath !== '/' ? rawPath.replace(/\/$/, '') : '/';

  if (ROUTE_META[path]) return ROUTE_META[path];

  const nauka = path.match(/^\/nauka\/([^/]+)\/([^/]+)$/);
  if (nauka) {
    const meta = metaPrzedmiotu(nauka[1], nauka[2]);
    if (meta) return meta;
  }

  // Podstrony pojedynczych pytań: /pytania/<slug>. Konkretny tytuł i opis
  // ustawia sam komponent strony (ma dostęp do treści pytania) - tutaj tylko
  // pilnujemy, żeby te adresy nie wpadły w domyślną regułę "noindex".
  if (path.startsWith('/pytania/')) {
    return {
      title: 'Pytania egzaminacyjne dla pilotów',
      description: 'Pytanie z egzaminu teoretycznego dla pilotów wraz z odpowiedzią i omówieniem.',
    };
  }

  // Trasy czysto aplikacyjne (pojedynek, ekrany wyników, zakup) - nie chcemy
  // ich w indeksie, ale linki z nich mają działać.
  return {
    title: 'Testy dla pilotów - Pilotuje.pl',
    description:
      'Testy online przygotowujące do egzaminów teoretycznych dla pilotów samolotowych i operatorów dronów.',
    robots: 'noindex, follow',
  };
};
